import Events from 'events';
import queryString from 'query-string';
import ShortUniqueId from 'short-unique-id';
import { Base64 } from 'js-base64';

const MODAL_LAYER_CLASS = 'smartwithfood-js-sdk-modal-layer';
const MODAL_LAYER_CLASS_ENTER = `${MODAL_LAYER_CLASS}--enter`;
const MODAL_LAYER_CLASS_PRELOADER = `${MODAL_LAYER_CLASS}__preloader`;
const MODAL_LAYER_ANIMATION_ROTATE = `${MODAL_LAYER_CLASS}-rotate`;
const PARTNER_CONTAINER_CLASS = 'partner-container';
const PARTNER_BUTTON_CLASS = 'partner-button';
const PARTNER_IMAGE_CLASS = 'partner-image';
const styles = `
.${MODAL_LAYER_CLASS} {
  position: fixed;
  overflow-y: scroll;
  overflow-scrolling: touch;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.28);
  transition: opacity 0.2s ease-in-out;
  opacity: 0;
}
.${MODAL_LAYER_CLASS_ENTER} {
  opacity: 1;
}
.${MODAL_LAYER_CLASS_PRELOADER} {
  display: block;
  position: absolute;
  z-index: 100;
  left: 50%;
  top: 50%;
  margin-top: -16px;
  margin-left: -16px;
  animation-name: ${MODAL_LAYER_ANIMATION_ROTATE};
  animation-duration: 0.8s;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
  border-color: rgba(255, 255, 255, 0.1);
  border-top-color: #12b7ff;
  background-color: transparent;
  border-radius: 16px;
  border-style: solid;
  border-width: 4px;
  box-sizing: border-box;
  transform: rotate(45deg);
  height: 32px;
  width: 32px;
}
@keyframes ${MODAL_LAYER_ANIMATION_ROTATE} {
  100% {
    transform: rotate(405deg);
  }
}
.${PARTNER_CONTAINER_CLASS} {
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
}
.${PARTNER_BUTTON_CLASS} {
  background-color: #F4F4F4;
  width: 5rem;
  height: 5rem;
  padding: 0.5rem;
  border: none;
  border-radius: 0.75rem;
  cursor: pointer;
}
.${PARTNER_IMAGE_CLASS} {
  max-width: 4rem;
  max-height: 4rem;
}
`;

var Language;
(function (Language) {
    Language["NL"] = "nl";
    Language["FR"] = "fr";
    Language["EN"] = "en";
})(Language || (Language = {}));

var ButtonType;
(function (ButtonType) {
    ButtonType["SINGLE_RECIPE"] = "single_recipe";
})(ButtonType || (ButtonType = {}));

function assert(expr, msg) {
    if (!expr) {
        throw new Error(msg || 'Assertion Failed');
    }
}

function getDomElement(selector) {
    if (typeof selector === 'string') {
        const element = document.querySelector(selector);
        if (!element) {
            throw new Error(`Could not find an element with selector "${selector}"`);
        }
        return element;
    }
    return selector;
}

function applyStyles(element, styles) {
    const styleString = Object.entries(styles)
        .map(([prop, value]) => `${prop}:${value}`)
        .join(';');
    element.setAttribute('style', styleString);
}

function createButton({ onClick, onHover }) {
    const button = document.createElement('button');
    button.className = PARTNER_BUTTON_CLASS;
    button.type = 'button';
    button.onclick = onClick;
    if (typeof onHover === 'function') {
        button.onmouseover = onHover;
    }
    return button;
}
function createButtonContainer({ style }) {
    const div = document.createElement('div');
    div.className = PARTNER_CONTAINER_CLASS;
    if (style) {
        applyStyles(div, style);
    }
    return div;
}
function createButtonImage({ url }) {
    const image = document.createElement('img');
    image.className = PARTNER_IMAGE_CLASS;
    image.src = url;
    return image;
}

function createLoader() {
    const loader = document.createElement('div');
    loader.className = MODAL_LAYER_CLASS_PRELOADER;
    return loader;
}
function createModal() {
    const modal = document.createElement('div');
    modal.className = MODAL_LAYER_CLASS;
    setTimeout(() => {
        modal.classList.add(MODAL_LAYER_CLASS_ENTER);
    }, 0);
    return modal;
}

function createIFrame(uri) {
    const iFrame = document.createElement('iframe');
    iFrame.setAttribute('frameborder', '0');
    iFrame.style.overflow = 'hidden';
    iFrame.style.display = 'block';
    // These sizes are initial
    // Right after App is mounted actual form size is transferred to iframe
    iFrame.setAttribute('width', '100%');
    iFrame.setAttribute('height', '100%');
    iFrame.setAttribute('src', uri);
    return iFrame;
}

var MessageType;
(function (MessageType) {
    MessageType["INITED"] = "INITED";
    MessageType["LOADED"] = "LOADED";
    MessageType["MODAL_CLOSED"] = "MODAL_CLOSED";
    MessageType["BASKET_COMPLETED"] = "BASKET_COMPLETED";
})(MessageType || (MessageType = {}));
function receiveMessages(callbackMap, globalCallback, widgetId) {
    function handler(event) {
        const { name, data } = event.data;
        // If we don't support he message type OR
        // If the id does not match this widget instance's id
        // Then we do not process the message
        const unknownType = !(name in MessageType);
        const unknownId = data.id !== widgetId;
        if (unknownType || unknownId) {
            console.debug(`Received message, will not process because: ${[
                unknownType ? 'unknown_type' : undefined,
                unknownId ? 'unknownId' : undefined,
            ]
                .filter(Boolean)
                .join(', ')}`, event.data);
            return;
        }
        else {
            console.debug('Received message', event.data);
        }
        if (globalCallback) {
            globalCallback(event.data);
        }
        const callback = callbackMap[name];
        if (callback) {
            callback(event.data);
        }
    }
    window.addEventListener('message', handler);
    return () => {
        window.removeEventListener('message', handler);
    };
}

function setNoScalableViewport() {
    const currentDocument = window.document;
    const isMobile = window.innerWidth < 640 || window.innerHeight < 510;
    const metaContent = isMobile
        ? 'width=device-width,initial-scale=1,maximum-scale=1,user-scalable=no,shrink-to-fit=no'
        : 'width=device-width,initial-scale=1';
    const meta = currentDocument.querySelector('meta[name="viewport"]');
    const oldMetaContent = meta && meta.content;
    if (meta) {
        meta.setAttribute('content', metaContent);
        meta.dataset.oldMetaContent = oldMetaContent !== null && oldMetaContent !== void 0 ? oldMetaContent : undefined;
        return;
    }
    const newMeta = currentDocument.createElement('meta');
    newMeta.setAttribute('name', 'viewport');
    newMeta.setAttribute('content', metaContent);
    currentDocument.head.appendChild(newMeta);
}
function unsetNoScalableViewport() {
    const currentDocument = window.document;
    const meta = currentDocument.querySelector('meta[name="viewport"]');
    if (meta && meta.dataset) {
        meta.setAttribute('content', meta.dataset.oldMetaContent || '');
    }
}

function camelize(str) {
    return str.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase());
}

function getScrollbarWidth() {
    var _a;
    const scrollTester = document.createElement('div');
    scrollTester.style.width = '50px';
    scrollTester.style.height = '50px';
    scrollTester.style.overflowY = 'scroll';
    document.body.appendChild(scrollTester);
    const result = scrollTester.offsetWidth - scrollTester.clientWidth;
    (_a = scrollTester === null || scrollTester === void 0 ? void 0 : scrollTester.parentNode) === null || _a === void 0 ? void 0 : _a.removeChild(scrollTester);
    return result;
}
function isPageScrollbarPresent() {
    const htmlNode = document.body.parentNode;
    const hasHtmlScroll = htmlNode.scrollHeight > htmlNode.clientHeight;
    const hasBodyScroll = document.body.scrollHeight > document.body.clientHeight;
    const htmlOverflowY = window.getComputedStyle(htmlNode).getPropertyValue('overflow-y');
    const bodyOverflowY = window.getComputedStyle(document.body).getPropertyValue('overflow-y');
    if (!hasHtmlScroll && !hasBodyScroll && htmlOverflowY !== 'scroll' && bodyOverflowY !== 'scroll') {
        return false;
    }
    return true;
}
function showBodyScrollbar() {
    document.body.style.overflow = '';
    document.body.style.marginRight = '';
}
function hideBodyScrollbar() {
    if (!isPageScrollbarPresent()) {
        return;
    }
    const scrollBarWidth = getScrollbarWidth();
    document.body.style.overflow = 'hidden';
    document.body.style.marginRight = `${scrollBarWidth}px`;
}

class Logger {
    constructor() {
        this.log = console.log;
        this.warn = console.warn;
        this.error = console.error;
        const isDev = 'production' === 'development';
        this.debugEnabled = isDev;
    }
    debug(...args) {
        if (this.debugEnabled) {
            console.debug(...args);
        }
    }
}
const logger = new Logger();

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function simpleFetch(input, init) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield fetch(input, Object.assign(Object.assign({}, init), { headers: Object.assign(Object.assign({}, init === null || init === void 0 ? void 0 : init.headers), { 'Content-Type': 'application/json' }) }));
        const data = yield response.json();
        return { data, response };
    });
}

const generateId = new ShortUniqueId({ length: 6 });
const BASE_URL = 'https://widget.smartwithfood.com/';
const VERSION = '2.0.2';
class WidgetInstance extends Events.EventEmitter {
    constructor(options) {
        super();
        this.id = generateId();
        this.token = options.token;
        this.language = options.language;
        this.devUrl = options.devUrl;
        this.devWidgetApiUrl = options.devWidgetApiUrl;
    }
    getBasketUri(options) {
        var _a;
        const { recipes, externalUrl, warehouseId, partnerId } = options;
        const query = Object.assign(Object.assign({ v: VERSION, i: this.id, t: this.token, l: this.language, p: partnerId, r: Base64.encode(JSON.stringify(recipes.map((recipe) => {
                if (typeof recipe === 'string') {
                    return recipe;
                }
                return {
                    n: recipe.name,
                    i: recipe.ingredients,
                    m: recipe.media,
                    y: recipe.yield,
                    l: recipe.language,
                };
            }))) }, (externalUrl ? { e: externalUrl } : { e: window.location.href })), (warehouseId ? { w: warehouseId } : {}));
        const base = (_a = this.devUrl) !== null && _a !== void 0 ? _a : BASE_URL;
        const basketUri = `${base}/${this.language}?${queryString.stringify(query)}`;
        logger.debug(basketUri.length, basketUri);
        return basketUri;
    }
    openModal(options) {
        const container = document.body;
        this.modal = createModal();
        container.appendChild(this.modal);
        this.loader = createLoader();
        this.modal.appendChild(this.loader);
        const iFrameUri = this.getBasketUri(options);
        this.iFrame = createIFrame(iFrameUri);
        this.modal.appendChild(this.iFrame);
        this._receiveMessagesFromWidget();
        hideBodyScrollbar();
        return () => this.closeModal();
    }
    closeModal() {
        unsetNoScalableViewport();
        showBodyScrollbar();
        if (this._cleanupMessageHandler) {
            this._cleanupMessageHandler();
            this._cleanupMessageHandler = undefined;
        }
        this._closeLoader();
        if (this.iFrame) {
            this.iFrame.remove();
            this.iFrame = undefined;
        }
        if (this.modal) {
            this.modal.remove();
            this.modal = undefined;
        }
    }
    renderButtons(_a) {
        var { selectorOrElement } = _a, options = __rest(_a, ["selectorOrElement"]);
        const container = selectorOrElement ? getDomElement(selectorOrElement) : document.body;
        this.buttonContainer = createButtonContainer({
            style: Object.assign({}, (selectorOrElement ? {} : { position: 'fixed', bottom: '1rem', right: '1rem' })),
        });
        container.append(this.buttonContainer);
        const controller = new AbortController();
        const url = this.devWidgetApiUrl ? this.devWidgetApiUrl : 'https://api.smartwithfood.com/widget/v1';
        simpleFetch(`${url}/widgets/${this.token}/partners`, {
            headers: {
                'Accept-Language': this.language,
            },
            signal: controller.signal,
        })
            .then(({ data }) => {
            data.partners.forEach((partner) => {
                logger.debug('Render partner button:', partner);
                if (this.buttonContainer) {
                    const button = this._renderButton(Object.assign(Object.assign({}, options), { partner }));
                    this.buttonContainer.appendChild(button);
                }
            });
        })
            .catch((error) => {
            console.error(error);
        });
        return () => {
            controller.abort();
            if (this.buttonContainer) {
                if (this.buttonContainer.hasChildNodes()) {
                    this.buttonContainer.childNodes.forEach((button) => {
                        if (button.hasChildNodes()) {
                            button.childNodes.forEach((image) => {
                                image.remove();
                            });
                        }
                        button.remove();
                    });
                }
                this.buttonContainer.remove();
                this.buttonContainer = undefined;
            }
        };
    }
    _renderButton(_a) {
        var { type = ButtonType.SINGLE_RECIPE } = _a, options = __rest(_a, ["type"]);
        switch (type) {
            case ButtonType.SINGLE_RECIPE:
                return this._createSingleRecipeButton(options);
            default:
                throw new Error(`Unknown button type "${type}".`);
        }
    }
    _createSingleRecipeButton(_a) {
        var { partner } = _a, options = __rest(_a, ["partner"]);
        const modalOptions = Object.assign(Object.assign({}, options), { partnerId: partner.id });
        const button = createButton({
            onClick: () => this.openModal(modalOptions),
        });
        const image = createButtonImage({
            url: partner.buttonLogoUrl,
        });
        button.appendChild(image);
        return button;
    }
    _receiveMessagesFromWidget() {
        this._cleanupMessageHandler = receiveMessages({
            [MessageType.INITED]: () => {
                setNoScalableViewport();
            },
            [MessageType.LOADED]: () => {
                this._closeLoader();
            },
            [MessageType.MODAL_CLOSED]: () => {
                this.closeModal();
            },
        }, (message) => {
            logger.debug(camelize(message.name), message.data);
            this.emit(camelize(message.name), message.data);
        }, this.id);
    }
    _closeLoader() {
        if (this.loader) {
            this.loader.remove();
            this.loader = undefined;
        }
    }
}

const INIT_ERROR_MESSAGE = 'Please initialize Recipe2Basket first using the ".initialize(options)" function';
var Status;
(function (Status) {
    Status["EMPTY"] = "empty";
    Status["INITIALIZED"] = "initialized";
})(Status || (Status = {}));
class Recipe2Basket extends Events.EventEmitter {
    constructor() {
        super(...arguments);
        this.status = Status.EMPTY;
    }
    initialize(options) {
        if (options.debug) {
            logger.debugEnabled = true;
        }
        const headElement = document.getElementsByTagName('head')[0];
        const styleElement = document.createElement('style');
        styleElement.innerHTML = styles;
        headElement.appendChild(styleElement);
        assert(options.token, 'options.token is required.');
        assert(options.language, 'options.language is required.');
        assert(Object.values(Language).includes(options.language), `options.language must be one of ${Object.values(Language).join(', ')}.`);
        logger.debug('Successfully initialized Recipe2Basket with options', options);
        this.token = options.token;
        this.language = options.language;
        this.devUrl = options.devUrl;
        this.devWidgetApiUrl = options.devWidgetApiUrl;
        this.status = Status.INITIALIZED;
    }
    getBasketUri(options) {
        this.checkStatus(); // All public functions should start with "this.checkStatus();"
        const widgetInstance = this.createWidgetInstance();
        this.currentInstance = widgetInstance;
        return widgetInstance.getBasketUri(options);
    }
    openModal(options) {
        this.checkStatus(); // All public functions should start with "this.checkStatus();"
        const widgetInstance = this.createWidgetInstance();
        this.currentInstance = widgetInstance;
        widgetInstance.openModal(options);
        return widgetInstance.id;
    }
    closeModal() {
        this.checkStatus(); // All public functions should start with "this.checkStatus();"
        if (!this.currentInstance) {
            throw new Error('Could not find a modal to close.');
        }
        this.currentInstance.closeModal();
        this.currentInstance = undefined;
    }
    renderButtons(options) {
        this.checkStatus(); // All public functions should start with "this.checkStatus();"
        const widgetInstance = this.createWidgetInstance();
        const renderButtonsCleanup = widgetInstance.renderButtons(options);
        return () => {
            renderButtonsCleanup();
        };
    }
    createWidgetInstance() {
        if (!this.token || !this.language) {
            throw new Error(INIT_ERROR_MESSAGE);
        }
        const widgetInstance = new WidgetInstance({
            token: this.token,
            language: this.language,
            devUrl: this.devUrl,
            devWidgetApiUrl: this.devWidgetApiUrl,
        });
        Object.values(MessageType).forEach((e) => {
            const eventName = camelize(e);
            widgetInstance.on(eventName, (...args) => {
                this.emit(eventName, ...args);
            });
        });
        return widgetInstance;
    }
    checkStatus() {
        assert(this.status === Status.INITIALIZED, INIT_ERROR_MESSAGE);
    }
}
var Recipe2Basket$1 = new Recipe2Basket();

// Public API
if (typeof window !== 'undefined') {
    window.Recipe2Basket = Recipe2Basket$1;
}

export { ButtonType, Language, Recipe2Basket$1 as default };
